import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Minus, ArrowCounterClockwise, Scales, Lightbulb, BookOpen } from "@phosphor-icons/react";
import { getConfig } from "@/lib/api";

const DEFAULT_OBJETO = (
  "EL PODERDANTE autoriza al APODERADO, a solicitar CERTIFICACIONES y "
  + "TÍTULOS PROVISIONALES, así como TÍTULOS DEFINITIVOS del inmueble "
  + "propiedad del PODERDANTE ubicados en {lugar_inmueble}, en el (IAD) "
  + "Instituto Agrario Dominicano y En el Registro de Título Correspondiente "
  + "y en todas las instituciones de la República Dominicana; a mi nombre, "
  + "sin ningún tipo de impedimento."
);

const blankApoderado = () => ({
  nombre: "", sexo: "M", nacionalidad: "dominicano", estado_civil: "soltero",
  cedula: "", codia: "", telefono: "", domicilio: "",
});

const blankTestigo = () => ({
  nombre: "", sexo: "M", nacionalidad: "dominicano", estado_civil: "soltero",
  cedula: "", domicilio: "",
});

function SexoConcordancia(prev, v) {
  // Devuelve un objeto con ajustes de nacionalidad y estado_civil al cambiar sexo
  const ajuste = {};
  if (v === "F") {
    if (prev.nacionalidad === "dominicano") ajuste.nacionalidad = "dominicana";
    if (prev.estado_civil === "soltero") ajuste.estado_civil = "soltera";
    if (prev.estado_civil === "casado") ajuste.estado_civil = "casada";
  } else {
    if (prev.nacionalidad === "dominicana") ajuste.nacionalidad = "dominicano";
    if (prev.estado_civil === "soltera") ajuste.estado_civil = "soltero";
    if (prev.estado_civil === "casada") ajuste.estado_civil = "casado";
  }
  return { ...prev, sexo: v, ...ajuste };
}

export default function PoderTab({ exp, setExp }) {
  const poder = exp.poder || {};
  // Si el expediente solo tiene el legacy 'apoderado' y no 'apoderados', migrar
  // en la vista (solo display, no persistente hasta guardar).
  const apoderadosFromState = poder.apoderados && poder.apoderados.length
    ? poder.apoderados
    : ((poder.apoderado && (poder.apoderado.nombre || "").trim())
        ? [poder.apoderado] : []);
  const poderdantes = poder.poderdantes || [];
  const testigos = poder.testigos || [];
  // Plantillas del perfil
  const [plantillas, setPlantillas] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getConfig().then((cfg) => {
      if (cancelled) return;
      const ag = cfg?.agrimensor || {};
      setPlantillas(ag.poder_plantillas || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const upd = (key, value) => setExp({ ...exp, poder: { ...poder, [key]: value } });
  const updApoderados = (next) => upd("apoderados", next);

  const addApoderado = () => updApoderados([...apoderadosFromState, blankApoderado()]);
  const delApoderado = (i) => updApoderados(apoderadosFromState.filter((_, idx) => idx !== i));
  const editApo = (i, field, value) => {
    const next = [...apoderadosFromState];
    next[i] = { ...next[i], [field]: value };
    updApoderados(next);
  };
  const editApoSexo = (i, v) => {
    const next = [...apoderadosFromState];
    next[i] = SexoConcordancia(next[i], v);
    updApoderados(next);
  };

  const usingDefault = apoderadosFromState.length === 0;

  return (
    <div className="space-y-6" data-testid="poder-tab">
      <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
        <div className="coord-label text-amber-800">// DOCUMENTO INDEPENDIENTE</div>
        <h3 className="text-lg font-bold tracking-tight text-amber-900 flex items-center gap-2">
          <Scales size={18} /> PODER ESPECIAL DE REPRESENTACIÓN
        </h3>
        <p className="text-sm text-amber-800 mt-1">
          Si no agregas <b>poderdantes</b>, se usará el cliente del expediente.
          Si no agregas <b>apoderados</b>, se usa el agrimensor del Perfil.
          Puedes agregar <b>N personas</b> en cualquiera de los dos lados, y elegir entre tus
          <b> plantillas</b> guardadas para el cuerpo PRIMERO.
        </p>
      </div>

      {/* Datos del acto */}
      <div className="bg-white border border-slate-200 p-5">
        <h4 className="font-bold text-slate-900 mb-3">Datos del Acto</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="coord-label">Fecha del Acto (DD/MM/AAAA)</Label>
            <Input
              data-testid="poder-fecha-acto"
              value={poder.fecha_acto || ""}
              onChange={(e) => upd("fecha_acto", e.target.value)}
              className="mt-1 rounded-sm font-mono"
              placeholder="17/06/2024 (vacío = hoy)"
            />
          </div>
          <div>
            <Label className="coord-label">Ubicación del Inmueble</Label>
            <Input
              data-testid="poder-lugar-inmueble"
              value={poder.lugar_inmueble || ""}
              onChange={(e) => upd("lugar_inmueble", e.target.value)}
              className="mt-1 rounded-sm"
              placeholder="Villa Altagracia, Provincia San Cristóbal"
            />
          </div>
        </div>
      </div>

      {/* Objeto del Poder (cláusula PRIMERO) */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div>
            <h4 className="font-bold text-slate-900">Objeto del Poder (cláusula PRIMERO)</h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Usa{" "}
              <code className="bg-slate-100 px-1 rounded">{"{lugar_inmueble}"}</code>{" "}
              como marcador. Guarda tus textos frecuentes como plantillas en el Perfil.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {plantillas.length > 0 && (
              <Select
                value=""
                onValueChange={(v) => {
                  const t = plantillas.find((p) => p.nombre === v);
                  if (t) upd("objeto_texto", t.texto);
                }}
              >
                <SelectTrigger data-testid="poder-plantilla-select" className="rounded-sm h-8 w-56">
                  <BookOpen size={12} className="mr-1" />
                  <SelectValue placeholder="Usar plantilla guardada..." />
                </SelectTrigger>
                <SelectContent>
                  {plantillas.map((p) => (
                    <SelectItem key={p.nombre} value={p.nombre}>{p.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              data-testid="poder-restaurar-objeto"
              type="button" size="sm" variant="outline"
              onClick={() => upd("objeto_texto", DEFAULT_OBJETO)}
              className="rounded-sm h-7"
            >
              <ArrowCounterClockwise size={12} className="mr-1" />Restaurar default
            </Button>
          </div>
        </div>
        <Textarea
          data-testid="poder-objeto-texto"
          value={poder.objeto_texto || DEFAULT_OBJETO}
          onChange={(e) => upd("objeto_texto", e.target.value)}
          className="rounded-sm font-mono text-sm min-h-[140px]"
        />
        <div className="text-xs text-slate-500 mt-2 flex items-start gap-1.5">
          <Lightbulb size={14} className="mt-0.5 text-amber-500" />
          <span>El placeholder <code>{"{lugar_inmueble}"}</code> se reemplazará con el campo "Ubicación del Inmueble" al generar el PDF. Para crear/editar plantillas: ve al Perfil → sección "Plantillas de Poder".</span>
        </div>
      </div>

      {/* PODERDANTES — lista ilimitada (override del cliente) */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h4 className="font-bold text-slate-900">Poderdantes ({poderdantes.length || "default"})</h4>
            <p className="text-xs text-slate-500 mt-0.5">
              {poderdantes.length === 0
                ? "Sin override — se usará el cliente del expediente como único PODERDANTE."
                : "Lista personalizada de poderdantes (ej. cónyuges otorgando juntos)."}
            </p>
          </div>
          <Button
            data-testid="poder-add-poderdante"
            type="button" size="sm" variant="outline"
            onClick={() => upd("poderdantes", [...poderdantes, blankApoderado()])}
            className="rounded-sm h-7"
          >
            <Plus size={12} className="mr-1" />Agregar poderdante
          </Button>
        </div>
        {poderdantes.length === 0 ? (
          <div className="text-xs text-slate-500 py-3 border border-dashed border-slate-300 rounded-sm text-center">
            El PODERDANTE será automáticamente el cliente del expediente (datos del tab "Datos").
          </div>
        ) : (
          <div className="space-y-3">
            {poderdantes.map((p, i) => (
              <div key={i} className="border border-slate-200 rounded-sm p-3" data-testid={`poder-poderdante-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-xs text-slate-500">PODERDANTE #{i + 1}</span>
                  <Button
                    data-testid={`poder-poderdante-del-${i}`}
                    type="button" size="sm" variant="ghost"
                    onClick={() => upd("poderdantes", poderdantes.filter((_, idx) => idx !== i))}
                    className="rounded-sm text-red-500 h-7"
                  ><Minus size={14} /></Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    data-testid={`poder-pod-nombre-${i}`}
                    value={p.nombre || ""}
                    onChange={(e) => {
                      const next = [...poderdantes]; next[i] = { ...next[i], nombre: e.target.value };
                      upd("poderdantes", next);
                    }}
                    placeholder="Nombre completo"
                    className="rounded-sm md:col-span-2"
                  />
                  <Select
                    value={p.sexo || "M"}
                    onValueChange={(v) => {
                      const next = [...poderdantes];
                      next[i] = SexoConcordancia(next[i], v);
                      upd("poderdantes", next);
                    }}
                  >
                    <SelectTrigger className="rounded-sm" data-testid={`poder-pod-sexo-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Masculino</SelectItem>
                      <SelectItem value="F">Femenino</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    data-testid={`poder-pod-cedula-${i}`}
                    value={p.cedula || ""}
                    onChange={(e) => {
                      const next = [...poderdantes]; next[i] = { ...next[i], cedula: e.target.value };
                      upd("poderdantes", next);
                    }}
                    placeholder="Cédula"
                    className="rounded-sm font-mono"
                  />
                  <Input
                    value={p.estado_civil || ""}
                    onChange={(e) => {
                      const next = [...poderdantes]; next[i] = { ...next[i], estado_civil: e.target.value };
                      upd("poderdantes", next);
                    }}
                    placeholder={p.sexo === "F" ? "soltera/casada/viuda..." : "soltero/casado/viudo..."}
                    className="rounded-sm"
                  />
                  <Input
                    data-testid={`poder-pod-domicilio-${i}`}
                    value={p.domicilio || ""}
                    onChange={(e) => {
                      const next = [...poderdantes]; next[i] = { ...next[i], domicilio: e.target.value };
                      upd("poderdantes", next);
                    }}
                    placeholder="Domicilio completo"
                    className="rounded-sm md:col-span-2"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* APODERADOS — lista ilimitada */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h4 className="font-bold text-slate-900">Apoderados ({apoderadosFromState.length || "default"})</h4>
            <p className="text-xs text-slate-500 mt-0.5">
              {usingDefault
                ? "Sin override — se usará el agrimensor del Perfil como único apoderado."
                : "Lista personalizada de apoderados para este expediente."}
            </p>
          </div>
          <Button
            data-testid="poder-add-apoderado"
            type="button" size="sm" variant="outline"
            onClick={addApoderado}
            className="rounded-sm h-7"
          >
            <Plus size={12} className="mr-1" />Agregar apoderado
          </Button>
        </div>
        {apoderadosFromState.length === 0 ? (
          <div className="text-xs text-slate-500 py-3 border border-dashed border-slate-300 rounded-sm text-center">
            El apoderado será automáticamente el agrimensor del Perfil.
          </div>
        ) : (
          <div className="space-y-3">
            {apoderadosFromState.map((a, i) => (
              <div key={i} className="border border-slate-200 rounded-sm p-3" data-testid={`poder-apoderado-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-xs text-slate-500">APODERADO #{i + 1}</span>
                  <Button
                    data-testid={`poder-apoderado-del-${i}`}
                    type="button" size="sm" variant="ghost"
                    onClick={() => delApoderado(i)}
                    className="rounded-sm text-red-500 h-7"
                  ><Minus size={14} /></Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    data-testid={`poder-apo-nombre-${i}`}
                    value={a.nombre || ""}
                    onChange={(e) => editApo(i, "nombre", e.target.value)}
                    placeholder="Nombre completo"
                    className="rounded-sm md:col-span-2"
                  />
                  <Select
                    value={a.sexo || "M"}
                    onValueChange={(v) => editApoSexo(i, v)}
                  >
                    <SelectTrigger className="rounded-sm" data-testid={`poder-apo-sexo-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Masculino</SelectItem>
                      <SelectItem value="F">Femenino</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    data-testid={`poder-apo-cedula-${i}`}
                    value={a.cedula || ""}
                    onChange={(e) => editApo(i, "cedula", e.target.value)}
                    placeholder="Cédula"
                    className="rounded-sm font-mono"
                  />
                  <Input
                    data-testid={`poder-apo-codia-${i}`}
                    value={a.codia || ""}
                    onChange={(e) => editApo(i, "codia", e.target.value)}
                    placeholder="CODIA (opcional)"
                    className="rounded-sm font-mono"
                  />
                  <Input
                    data-testid={`poder-apo-telefono-${i}`}
                    value={a.telefono || ""}
                    onChange={(e) => editApo(i, "telefono", e.target.value)}
                    placeholder="Teléfono(s) (opcional)"
                    className="rounded-sm font-mono"
                  />
                  <Input
                    value={a.estado_civil || ""}
                    onChange={(e) => editApo(i, "estado_civil", e.target.value)}
                    placeholder={a.sexo === "F" ? "soltera/casada..." : "soltero/casado..."}
                    className="rounded-sm"
                  />
                  <Input
                    data-testid={`poder-apo-domicilio-${i}`}
                    value={a.domicilio || ""}
                    onChange={(e) => editApo(i, "domicilio", e.target.value)}
                    placeholder="Domicilio completo"
                    className="rounded-sm md:col-span-2"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* TESTIGOS del Poder */}
      <div className="bg-white border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h4 className="font-bold text-slate-900">Testigos del Poder</h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Si dejas vacío, se usan los "Testigos por Defecto" del Perfil (sección Duplicado).
            </p>
          </div>
          <Button
            data-testid="poder-add-testigo"
            type="button" size="sm" variant="outline"
            onClick={() => upd("testigos", [...testigos, blankTestigo()])}
            className="rounded-sm h-7"
          >
            <Plus size={12} className="mr-1" />Sobrescribir testigo
          </Button>
        </div>
        {testigos.length === 0 ? (
          <div className="text-xs text-slate-500 py-3 border border-dashed border-slate-300 rounded-sm text-center">
            Usando los testigos del Perfil.
          </div>
        ) : (
          <div className="space-y-3">
            {testigos.map((t, i) => (
              <div key={i} className="border border-slate-200 rounded-sm p-3" data-testid={`poder-testigo-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-xs text-slate-500">TESTIGO #{i + 1}</span>
                  <Button
                    data-testid={`poder-testigo-del-${i}`}
                    type="button" size="sm" variant="ghost"
                    onClick={() => upd("testigos", testigos.filter((_, idx) => idx !== i))}
                    className="rounded-sm text-red-500 h-7"
                  ><Minus size={14} /></Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    value={t.nombre || ""}
                    onChange={(e) => {
                      const next = [...testigos]; next[i] = { ...next[i], nombre: e.target.value };
                      upd("testigos", next);
                    }}
                    placeholder="Nombre completo"
                    className="rounded-sm md:col-span-2"
                  />
                  <Select
                    value={t.sexo || "M"}
                    onValueChange={(v) => {
                      const next = [...testigos];
                      next[i] = SexoConcordancia(next[i], v);
                      upd("testigos", next);
                    }}
                  >
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Masculino</SelectItem>
                      <SelectItem value="F">Femenino</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    value={t.cedula || ""}
                    onChange={(e) => {
                      const next = [...testigos]; next[i] = { ...next[i], cedula: e.target.value };
                      upd("testigos", next);
                    }}
                    placeholder="Cédula"
                    className="rounded-sm font-mono"
                  />
                  <Input
                    value={t.estado_civil || ""}
                    onChange={(e) => {
                      const next = [...testigos]; next[i] = { ...next[i], estado_civil: e.target.value };
                      upd("testigos", next);
                    }}
                    placeholder={t.sexo === "F" ? "soltera/casada..." : "soltero/casado..."}
                    className="rounded-sm"
                  />
                  <Input
                    value={t.domicilio || ""}
                    onChange={(e) => {
                      const next = [...testigos]; next[i] = { ...next[i], domicilio: e.target.value };
                      upd("testigos", next);
                    }}
                    placeholder="Domicilio completo"
                    className="rounded-sm md:col-span-2"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-xs text-slate-500 italic">
        El <b>notario</b> se toma del tab "Datos" del expediente. Si no añades poderdantes, se usa el cliente del expediente.
      </div>
    </div>
  );
}
